// Reports → Overview (F-ANL-03, spec/17 R-01): the headline figures against the previous period — revenue, money in,
// sessions, occupancy, average session length and revenue per player, each with its delta and its days as a
// sparkline — the revenue trend beside how it was paid (a donut), sessions each day against their average beside the
// busiest hours (the hour × weekday heatmap), and, rolled up, each venue side by side.
//
// Revenue is the one definition (M-01): wallet spends count when spent, a top-up is money in (held for the player), bonus
// spent is a promotional cost. A role without `reports:financial` sees the sessions half only.

import type { RevenueReport, SessionsReport } from '@clavian/api-client';
import { revenuePerPlayerOf } from '@clavian/domain/reports';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { ChartEmpty, ChartTable, type ChartTone, ColumnChart, DonutChart, Heatmap, LineChart, StatCard, Table, type TableColumn } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportNote, ReportPage } from './report-kit';
import { type ReportScope, useRevenueReport, useSessionsReport } from './queries';
import { averageOf, dayLong, dayShort, deltaOf, moneyOrDash, moneyShort, moneyTick, NO_DATA, percentOf, shareWords, sparkOf, TabEmpty, TabPending, type Format } from './tab-kit';

/** How revenue was paid, in the chart kit's fixed order: the counter's tenders, online, then wallets in the neutral. */
export const SOURCE_TONE: Readonly<Record<string, ChartTone>> = { cash: 1, counter_upi: 2, counter_card: 3, online: 4, wallet: 5 };
const SOURCE_ORDER = ['cash', 'counter_upi', 'counter_card', 'online', 'wallet'] as const;

export function OverviewTab({ scope, previousLabel, canFinancial }: { readonly scope: ReportScope; readonly previousLabel: string; readonly canFinancial: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const revenue = useRevenueReport(scope, canFinancial);
  const sessions = useSessionsReport(scope, true);
  if (sessions.data === undefined || (canFinancial && revenue.data === undefined)) {
    const failed = sessions.data === undefined ? sessions : revenue;
    return <TabPending query={failed} unavailable={t('reports.unavailable')} forbidden={t('reports.notPermitted')} />;
  }
  const money = canFinancial ? revenue.data : undefined;
  const empty = (money === undefined || money.totals === null) && sessions.data.totals.sessions === null;
  if (empty) return <TabEmpty title={t('reports.emptyTitle')} description={t('reports.empty')} />;
  return <Overview revenue={money} sessions={sessions.data} previousLabel={previousLabel} />;
}

function Overview({ revenue, sessions, previousLabel }: { readonly revenue: RevenueReport | undefined; readonly sessions: SessionsReport; readonly previousLabel: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const currency = revenue?.currency ?? sessions.currency;
  const short = (amount: number) => moneyShort(format, amount, currency);
  const caption = t('reports.vsPrevious', { range: previousLabel });
  const totals = revenue?.totals ?? null;
  const previous = revenue?.previous ?? null;
  const now = sessions.totals;
  const before = sessions.previous;
  const arpu = revenuePerPlayerOf(totals === null ? null : BigInt(totals.revenue.amount), now.activePlayers);
  const arpuBefore = revenuePerPlayerOf(previous === null ? null : BigInt(previous.revenue.amount), before.activePlayers);
  const averageByDay = sessions.byDay.map((day) => (day.sessions === 0 ? null : Math.round(day.playSeconds / day.sessions)));

  return (
    <ReportPage>
      {revenue?.partial === true || sessions.partial ? <ReportNote>{t('reports.partial')}</ReportNote> : null}
      <ReportKpis label={t('reports.tab.overview')} columns={3}>
        {revenue !== undefined ? (
          <>
            <StatCard
              label={t('reports.kpi.revenue')}
              value={<KpiFigure value={totals?.revenue.amount} format={short} />}
              {...withDelta(deltaOf(totals?.revenue.amount, previous?.revenue.amount, caption, format))}
              hint={totals === null ? t('reports.noData') : t('reports.kpi.revenueHint', { wallet: format.money(totals.walletSpent) })}
              {...sparkOf(revenue.byDay.map((day) => day.revenue?.amount))}
            />
            <StatCard
              label={t('reports.kpi.moneyIn')}
              value={<KpiFigure value={totals?.moneyIn.amount} format={short} />}
              {...withDelta(deltaOf(totals?.moneyIn.amount, previous?.moneyIn.amount, caption, format))}
              hint={totals === null ? t('reports.noData') : t('reports.kpi.moneyInHint', { topUps: format.money(totals.walletTopUps) })}
              {...sparkOf(revenue.byDay.map((day) => day.moneyIn?.amount))}
            />
          </>
        ) : null}
        <StatCard
          label={t('reports.kpi.sessions')}
          value={<KpiFigure value={now.sessions} format={(n) => format.number(n)} />}
          {...withDelta(deltaOf(now.sessions, before.sessions, caption, format))}
          hint={now.sessions === null ? t('reports.noData') : t('reports.kpi.sessionsHint', { players: now.activePlayers ?? 0, fresh: now.newPlayers ?? 0 })}
          {...sparkOf(sessions.byDay.map((day) => day.sessions))}
        />
        <StatCard
          label={t('reports.kpi.occupancy')}
          value={<KpiFigure value={now.occupancyBasisPoints} format={(bp) => percentOf(format, bp, 1)} />}
          {...withDelta(deltaOf(now.occupancyBasisPoints, before.occupancyBasisPoints, caption, format))}
          hint={sessions.hoursSet ? t('reports.kpi.occupancyHint') : t('reports.kpi.occupancyNoHours')}
        />
        <StatCard
          label={t('reports.kpi.averageSession')}
          value={<KpiFigure value={now.averageSeconds} format={(n) => format.duration(n)} />}
          {...withDelta(deltaOf(now.averageSeconds, before.averageSeconds, caption, format))}
          hint={now.playSeconds === null ? t('reports.noData') : t('reports.kpi.playTime', { time: format.duration(now.playSeconds) })}
          {...sparkOf(averageByDay)}
        />
        {revenue !== undefined ? (
          <StatCard
            label={t('reports.kpi.arpu')}
            value={<KpiFigure value={arpu === null ? null : Number(arpu)} format={short} />}
            {...withDelta(deltaOf(arpu === null ? null : Number(arpu), arpuBefore === null ? null : Number(arpuBefore), caption, format))}
            hint={t('reports.kpi.arpuHint')}
          />
        ) : null}
      </ReportKpis>

      {revenue !== undefined ? (
        <ReportGrid layout="lead">
          <ReportCard title={t('reports.revenue.trend')} description={t('reports.revenue.trendHint')}>
            <LineChart
              label={t('reports.revenue.trend')}
              summary={trendSummary(t, revenue.byDay.map((day) => day.revenue?.amount ?? null), (index) => dayLong(format, revenue.byDay[index]?.date ?? ''), short)}
              categories={revenue.byDay.map((day) => dayShort(format, day.date))}
              categoryLabel={(index) => dayLong(format, revenue.byDay[index]?.date ?? '')}
              series={[
                { key: 'revenue', label: t('reports.kpi.revenue'), tone: 1, area: true, values: revenue.byDay.map((day) => day.revenue?.amount ?? null) },
                { key: 'moneyIn', label: t('reports.kpi.moneyIn'), tone: 5, values: revenue.byDay.map((day) => day.moneyIn?.amount ?? null) },
              ]}
              format={short}
              formatTick={(value) => moneyTick(format, value, currency)}
            />
            <ChartTable
              caption={t('reports.revenue.trend')}
              rows={revenue.byDay}
              rowKey={(row) => row.date}
              columns={[
                { key: 'date', header: t('reports.column.day'), cell: (row) => dayLong(format, row.date) },
                { key: 'revenue', header: t('reports.kpi.revenue'), numeric: true, cell: (row) => <span className="console-money">{moneyOrDash(format, row.revenue)}</span> },
                { key: 'moneyIn', header: t('reports.kpi.moneyIn'), numeric: true, cell: (row) => <span className="console-money">{moneyOrDash(format, row.moneyIn)}</span> },
                { key: 'wallet', header: t('reports.column.walletSpent'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.walletSpent)}</span> },
              ]}
            />
          </ReportCard>
          <PaidByCard revenue={revenue} />
        </ReportGrid>
      ) : null}

      <ReportGrid layout="even">
        <SessionsByDayCard sessions={sessions} />
        <BusiestHoursCard sessions={sessions} />
      </ReportGrid>

      {revenue !== undefined && revenue.byVenue.length > 1 ? <VenuesTable revenue={revenue} /> : null}
    </ReportPage>
  );
}

/** A StatCard's `delta` prop only when there is one (exactOptionalPropertyTypes). */
export function withDelta(delta: ReturnType<typeof deltaOf>): { delta?: NonNullable<ReturnType<typeof deltaOf>> } {
  return delta === undefined ? {} : { delta };
}

/** A clock hour in words: "9 pm". */
export function hourWords(format: Format, hour: number): string {
  // A clock hour, not an instant: formatted in UTC so no timezone moves it.
  return new Intl.DateTimeFormat(format.locale, { hour: 'numeric', timeZone: 'UTC' }).format(Date.UTC(2026, 0, 5, hour));
}

/** What a trend says, in a sentence, for a screen reader: where it started, where it ended and its highest day. */
export function trendSummary(
  t: ReturnType<typeof useTranslation>['t'],
  values: readonly (number | null)[],
  labelAt: (index: number) => string,
  print: (value: number) => string,
): string | undefined {
  const known = values.map((value, index) => ({ value, index })).filter((point): point is { value: number; index: number } => point.value !== null);
  const first = known[0];
  const last = known[known.length - 1];
  if (first === undefined || last === undefined) return undefined;
  const top = known.reduce((best, point) => (point.value > best.value ? point : best), first);
  return t('reports.chart.summary', { first: print(first.value), firstDay: labelAt(first.index), last: print(last.value), lastDay: labelAt(last.index), max: print(top.value), maxDay: labelAt(top.index) });
}

/** How revenue was paid: the venue's tenders, online, and wallets (money spent from them) — a donut with % and value. */
export function PaidByCard({ revenue }: { readonly revenue: RevenueReport }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const sources = revenue.totals?.bySource ?? [];
  const slices = SOURCE_ORDER.map((tender) => sources.find((row) => row.tender === tender))
    .filter((row): row is NonNullable<typeof row> => row !== undefined && row.amount.amount !== 0)
    .map((row) => ({
      key: row.tender,
      label: t(`reports.tender.${row.tender}`),
      value: row.amount.amount,
      display: format.money(row.amount),
      tone: SOURCE_TONE[row.tender] ?? 5,
      ...(row.tender === 'wallet' ? { hint: t('reports.revenue.walletSliceHint') } : {}),
    }));
  const negative = slices.filter((slice) => slice.value < 0);
  return (
    <ReportCard title={t('reports.revenue.paidBy')} description={t('reports.revenue.paidByHint')}>
      {slices.length === 0 || revenue.totals === null ? (
        <ChartEmpty>{t('reports.noData')}</ChartEmpty>
      ) : (
        <DonutChart
          label={t('reports.revenue.paidBy')}
          slices={slices}
          total={{ display: moneyShort(format, revenue.totals.revenue.amount, revenue.currency), label: t('reports.kpi.revenue') }}
          percent={(share) => shareWords(format, share)}
        />
      )}
      {negative.length > 0 ? <ReportNote>{t('reports.revenue.negativeNote', { tenders: negative.map((slice) => slice.label).join(', ') })}</ReportNote> : null}
      {revenue.totals !== null && revenue.totals.bonusSpent.amount > 0 ? <ReportNote>{t('reports.revenue.bonusNote', { amount: format.money(revenue.totals.bonusSpent) })}</ReportNote> : null}
    </ReportCard>
  );
}

/** Sessions each business day as columns, the count on each, and the period's daily average across them. */
export function SessionsByDayCard({ sessions, height = 200 }: { readonly sessions: SessionsReport; readonly height?: number }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const counts = sessions.byDay.map((day) => day.sessions);
  // The average of the days that have happened: up to the last day with a session (a range may run past today).
  const lastActive = counts.reduce((last, count, index) => (count > 0 ? index : last), -1);
  const average = averageOf(counts.slice(0, lastActive + 1));
  return (
    <ReportCard title={t('reports.sessions.byDay')} description={t('reports.businessDays')}>
      {counts.every((count) => count === 0) ? (
        <ChartEmpty>{t('reports.sessions.noSessions')}</ChartEmpty>
      ) : (
        <ColumnChart
          label={t('reports.sessions.byDay')}
          summary={trendSummary(t, counts, (index) => dayLong(format, sessions.byDay[index]?.date ?? ''), (value) => format.number(value))}
          categories={sessions.byDay.map((day) => dayShort(format, day.date))}
          categoryLabel={(index) => dayLong(format, sessions.byDay[index]?.date ?? '')}
          series={[{ key: 'sessions', label: t('reports.kpi.sessions'), tone: 1, values: counts }]}
          format={(value) => format.number(value)}
          integer
          height={height}
          {...(average === null ? {} : { reference: { value: average, label: t('reports.sessions.average', { value: format.number(average, { maximumFractionDigits: 0 }) }) } })}
        />
      )}
      <ChartTable
        caption={t('reports.sessions.byDay')}
        rows={sessions.byDay}
        rowKey={(row) => row.date}
        columns={[
          { key: 'date', header: t('reports.column.day'), cell: (row) => dayLong(format, row.date) },
          { key: 'sessions', header: t('reports.kpi.sessions'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.sessions)}</span> },
          { key: 'play', header: t('reports.column.playTime'), numeric: true, cell: (row) => <span className="console-num">{format.duration(row.playSeconds)}</span> },
        ]}
      />
    </ReportCard>
  );
}

/** The busiest hours: the hour × weekday heatmap, compact, with the peak named in the description. */
function BusiestHoursCard({ sessions }: { readonly sessions: SessionsReport }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const peak = sessions.peaks[0];
  const weekday = (index: number, long = false) => t(long ? `reports.weekdayLong.${index}` : `reports.weekday.${index}`);
  const max = Math.max(0, ...sessions.heatmap.map((cell) => cell.basisPoints ?? 0));
  return (
    <ReportCard
      title={t('reports.sessions.peaks')}
      description={peak === undefined ? t('reports.sessions.peaksHint') : t('reports.sessions.peakMeta', { day: weekday(peak.weekday, true), hour: hourWords(format, peak.hour), occupancy: percentOf(format, peak.basisPoints) })}
    >
      {max === 0 ? (
        <ChartEmpty>{t('reports.sessions.noPeaks')}</ChartEmpty>
      ) : (
        <Heatmap
          label={t('reports.sessions.peaks')}
          summary={peak === undefined ? undefined : t('reports.sessions.peakMeta', { day: weekday(peak.weekday, true), hour: hourWords(format, peak.hour), occupancy: percentOf(format, peak.basisPoints) })}
          cells={sessions.heatmap.map((cell) => ({ weekday: cell.weekday, hour: cell.hour, value: cell.basisPoints }))}
          firstHour={sessions.firstHour}
          weekdayLabel={(index) => weekday(index)}
          hourLabel={(hour) => hourWords(format, hour)}
          describeCell={(cell) =>
            cell.value === null
              ? t('reports.sessions.cellClosed', { day: weekday(cell.weekday, true), hour: hourWords(format, cell.hour) })
              : t('reports.sessions.cell', { day: weekday(cell.weekday, true), hour: hourWords(format, cell.hour), occupancy: percentOf(format, cell.value) })
          }
          max={max}
          formatValue={(value) => percentOf(format, value)}
          closedLabel={t('reports.sessions.legendClosed')}
          compact
        />
      )}
      <ChartTable
        caption={t('reports.sessions.peaks')}
        rows={sessions.peaks}
        rowKey={(row) => `${row.weekday}-${row.hour}`}
        columns={[
          { key: 'when', header: t('reports.column.weekday'), cell: (row) => t('reports.sessions.peakAt', { day: weekday(row.weekday, true), hour: hourWords(format, row.hour) }) },
          { key: 'occupancy', header: t('reports.kpi.occupancy'), numeric: true, cell: (row) => <span className="console-num">{percentOf(format, row.basisPoints)}</span> },
        ]}
      />
    </ReportCard>
  );
}

type VenueLine = RevenueReport['byVenue'][number];

/** Each venue side by side, most revenue first (spec/13 §15 "compare venues side by side"). */
function VenuesTable({ revenue }: { readonly revenue: RevenueReport }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const caption = t('reports.vsPrevious', { range: '' });
  const rows = [...revenue.byVenue].sort((a, b) => (b.revenue?.amount ?? -Infinity) - (a.revenue?.amount ?? -Infinity) || a.name.localeCompare(b.name));
  const columns: readonly TableColumn<VenueLine>[] = [
    { key: 'venue', header: t('reports.column.venue'), cell: (row) => row.name },
    { key: 'revenue', header: t('reports.kpi.revenue'), numeric: true, cell: (row) => <span className="console-money">{moneyOrDash(format, row.revenue)}</span> },
    {
      key: 'change',
      header: t('reports.column.change'),
      numeric: true,
      cell: (row) => {
        const delta = deltaOf(row.revenue?.amount, row.previousRevenue?.amount, caption, format);
        return <span className="console-num">{delta === undefined ? NO_DATA : `${delta.trend === 'down' ? '−' : delta.trend === 'up' ? '+' : ''}${delta.value}`}</span>;
      },
    },
    { key: 'moneyIn', header: t('reports.kpi.moneyIn'), numeric: true, cell: (row) => <span className="console-money">{moneyOrDash(format, row.moneyIn)}</span> },
  ];
  return (
    <ReportCard title={t('reports.byVenue')} description={t('reports.byVenueHint')} flush>
      <Table caption={t('reports.byVenue')} captionHidden columns={columns} rows={rows} rowKey={(row) => row.venueId} empty={t('reports.noData')} />
    </ReportCard>
  );
}
